import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Brain, CreditCard, CheckCircle2, MapPin, Ticket, Loader2, X, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentPage = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  
  const paymentType = searchParams.get('type') || 'career_mapping';
  const isCareerMapping = paymentType === 'career_mapping';

  useEffect(() => {
    // Check if already paid for career mapping
    if (isCareerMapping && user?.career_mapping_paid) {
      navigate('/dashboard');
    }
    // Check if eligible for career mapping (grades 10-12)
    if (isCareerMapping && !['10', '11', '12'].includes(user?.grade)) {
      toast.error('Career Mapping is only available for grades 10-12');
      navigate('/dashboard');
    }
  }, [user, navigate, isCareerMapping]);

  const handleValidateCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }
    
    setValidatingCoupon(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/payment/validate-coupon`,
        { code: couponCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCouponApplied(response.data);
      toast.success(`Coupon applied! ${response.data.discount_percent}% discount`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid coupon code');
      setCouponApplied(null);
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponCode('');
    setCouponApplied(null);
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async () => {
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/payment/create-order`,
        { 
          payment_type: paymentType,
          coupon_code: couponApplied?.code || null
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Check if free access was granted (100% coupon)
      if (response.data.free_access) {
        const updatedUser = { ...user, career_mapping_paid: true };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUser(updatedUser);
        toast.success(response.data.message);
        navigate('/dashboard');
        return;
      }

      setOrderData(response.data);

      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error('Failed to load payment gateway');
        setLoading(false);
        return;
      }

      // Check if this is a mock payment (when Razorpay keys not provided)
      if (response.data.key_id === 'MOCK_KEY_FOR_TESTING') {
        toast.info('Using mock payment mode (Razorpay keys not configured)');
        
        // Simulate payment success after 2 seconds
        setTimeout(async () => {
          try {
            await axios.post(
              `${API}/payment/verify`,
              {
                order_id: response.data.order_id,
                payment_id: 'mock_payment_id',
                signature: 'mock_signature',
                payment_type: paymentType
              },
              {
                headers: { Authorization: `Bearer ${token}` }
              }
            );

            // Update user state
            const updatedUser = { 
              ...user, 
              ...(isCareerMapping ? { career_mapping_paid: true } : { has_paid: true })
            };
            localStorage.setItem('user', JSON.stringify(updatedUser));
            setUser(updatedUser);

            toast.success('Payment successful! (Mock Mode)');
            navigate('/dashboard');
          } catch (error) {
            toast.error('Payment verification failed');
          }
          setLoading(false);
        }, 2000);
        return;
      }

      // Real Razorpay payment
      const options = {
        key: response.data.key_id,
        amount: response.data.amount,
        currency: response.data.currency,
        name: 'Uxima Foundation',
        description: isCareerMapping ? 'Career Mapping Assessment' : 'Psychometric Test Payment',
        order_id: response.data.order_id,
        handler: async function (razorpayResponse) {
          try {
            await axios.post(
              `${API}/payment/verify`,
              {
                order_id: razorpayResponse.razorpay_order_id,
                payment_id: razorpayResponse.razorpay_payment_id,
                signature: razorpayResponse.razorpay_signature,
                payment_type: paymentType
              },
              {
                headers: { Authorization: `Bearer ${token}` }
              }
            );

            // Update user state
            const updatedUser = { 
              ...user, 
              ...(isCareerMapping ? { career_mapping_paid: true } : { has_paid: true })
            };
            localStorage.setItem('user', JSON.stringify(updatedUser));
            setUser(updatedUser);

            toast.success('Payment successful!');
            navigate('/dashboard');
          } catch (error) {
            toast.error('Payment verification failed');
          }
          setLoading(false);
        },
        prefill: {
          name: user?.name,
          email: user?.email
        },
        theme: {
          color: '#8b5cf6'
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', function () {
        toast.error('Payment failed. Please try again.');
        setLoading(false);
      });
      razorpay.open();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment initiation failed');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-indigo-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-xl border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Brain className="w-8 h-8 text-indigo-500" />
            <h1 className="font-heading text-2xl font-bold text-slate-900">PsychTest</h1>
          </div>
          <Button
            data-testid="back-to-dashboard-btn"
            onClick={() => navigate('/dashboard')}
            variant="ghost"
          >
            Back to Dashboard
          </Button>
        </div>
      </nav>

      {/* Payment Content */}
      <div className="max-w-2xl mx-auto px-6 py-12">
        <Card className="bg-white/80 backdrop-blur-md rounded-2xl border border-violet-200 shadow-xl">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              {isCareerMapping ? (
                <MapPin className="w-16 h-16 text-violet-500" />
              ) : (
                <CreditCard className="w-16 h-16 text-indigo-500" />
              )}
            </div>
            <CardTitle className="font-heading text-2xl sm:text-3xl">
              {isCareerMapping ? 'Unlock Career Mapping' : 'Complete Your Payment'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-gradient-to-r from-violet-50 to-purple-50 rounded-xl p-6 border border-violet-100">
              <h3 className="font-heading text-lg font-semibold text-slate-900 mb-4">
                {isCareerMapping ? 'Career Mapping Program' : 'Psychometric Test Access'}
              </h3>
              {isCareerMapping ? (
                <ul className="space-y-3 text-slate-700">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-violet-500" />
                    90-question comprehensive assessment
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-violet-500" />
                    Detailed career guidance report
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-violet-500" />
                    Personalized 30-day action plan
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-violet-500" />
                    Career counseling by Dr. Joy Sardar (Ph.D Education)
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-violet-500" />
                    Stream selection guidance
                  </li>
                </ul>
              ) : (
                <ul className="space-y-3 text-slate-700">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-teal-500" />
                    30-minute comprehensive assessment
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-teal-500" />
                    Multiple category questions
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-teal-500" />
                    Detailed performance report
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-teal-500" />
                    Category-wise score analysis
                  </li>
                </ul>
              )}
            </div>

            {/* Coupon Code Section */}
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-2 mb-3">
                <Ticket className="w-5 h-5 text-violet-500" />
                <span className="font-medium text-slate-700">Have a coupon code?</span>
              </div>
              
              {couponApplied ? (
                <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-green-700">
                      {couponApplied.code} - {couponApplied.discount_percent}% OFF
                    </span>
                  </div>
                  <Button
                    data-testid="remove-coupon-btn"
                    onClick={handleRemoveCoupon}
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    data-testid="coupon-input"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="Enter coupon code"
                    className="flex-1 uppercase rounded-lg border-slate-200"
                  />
                  <Button
                    data-testid="apply-coupon-btn"
                    onClick={handleValidateCoupon}
                    disabled={validatingCoupon || !couponCode.trim()}
                    variant="outline"
                    className="border-violet-200 text-violet-600 hover:bg-violet-50"
                  >
                    {validatingCoupon ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Apply'
                    )}
                  </Button>
                </div>
              )}
            </div>

            {/* Pricing Section */}
            <div className="py-4 border-t border-b border-slate-200 space-y-2">
              <div className="flex items-center justify-between text-slate-600">
                <span>Original Price</span>
                <span className={couponApplied ? 'line-through' : ''}>₹{isCareerMapping ? '199' : '299'}</span>
              </div>
              
              {couponApplied && (
                <div className="flex items-center justify-between text-green-600">
                  <span>Discount ({couponApplied.discount_percent}%)</span>
                  <span>- ₹{couponApplied.discount_amount}</span>
                </div>
              )}
              
              <div className="flex items-center justify-between pt-2">
                <span className="text-lg font-semibold text-slate-900">Total Amount</span>
                <span className="font-heading text-3xl font-bold text-violet-600">
                  {couponApplied?.is_free ? (
                    <span className="text-green-600">FREE</span>
                  ) : (
                    `₹${couponApplied ? couponApplied.final_amount : (isCareerMapping ? '199' : '299')}`
                  )}
                </span>
              </div>
            </div>

            <Button
              data-testid="proceed-to-pay-btn"
              onClick={handlePayment}
              disabled={loading}
              className={`w-full rounded-full px-8 py-4 ${
                couponApplied?.is_free
                  ? 'bg-green-500 hover:bg-green-600 shadow-green-500/20'
                  : isCareerMapping 
                    ? 'bg-violet-500 hover:bg-violet-600 shadow-violet-500/20' 
                    : 'bg-indigo-500 hover:bg-indigo-600 shadow-indigo-500/20'
              } text-white font-semibold text-lg transition-all hover:scale-105 shadow-lg`}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin inline" />
                  Processing...
                </>
              ) : couponApplied?.is_free ? (
                'Get Free Access'
              ) : (
                `Proceed to Pay ₹${couponApplied ? couponApplied.final_amount : (isCareerMapping ? '199' : '299')}`
              )}
            </Button>

            <p className="text-center text-sm text-slate-500">
              {couponApplied?.is_free ? 'Click above to unlock your free access' : 'Secure payment powered by Razorpay'}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PaymentPage;
